import React, { useEffect, useMemo, useRef, useState } from "react";

const INTRO = "Next, we will ask about two fields from the form you just submitted. The fact that a field appears here does not mean that it was correct or incorrect. Please answer about what you remember doing during the final review—not what you would do now. “I do not remember” is a useful answer. Please do not look anything up.";

const OPTIONS = {
  F1: [
    ["DELIBERATE_CHECK", "I deliberately checked the value against other information."],
    ["READ_ONLY", "I read or looked at the value but did not check it against other information."],
    ["NO_REVIEW", "I did not read or look at this field during the final review."],
    ["OTHER", "Something else"],
    ["DO_NOT_REMEMBER", "I do not remember."],
  ],
  F2: [
    ["SOURCE_PACKET", "The scenario."],
    ["OWN_CHAT", "Information in my own earlier chat messages."],
    ["ASSISTANT_CHAT", "Information in the assistant's earlier chat messages."],
    ["MEMORY", "My memory of the incident details."],
    ["FORM_VALUE", "The value already shown in the form."],
    ["OTHER", "Something else"],
    ["NO_BASIS", "No particular information."],
    ["DO_NOT_REMEMBER", "I do not remember."],
  ],
  F4: [
    ["MATCH", "I thought it matched the information I was relying on."],
    ["MISMATCH", "I thought it did not match the information I was relying on."],
    ["NO_JUDGMENT", "I did not make a match-or-mismatch judgment."],
    ["OTHER", "Something else"],
    ["DO_NOT_REMEMBER", "I do not remember."],
  ],
  F5: [
    ["CHANGED", "I changed the field."],
    ["ATTEMPT_FAILED", "I tried to change it, but the change did not remain."],
    ["DEFERRED", "I intended to change it later but did not."],
    ["LEFT_UNCHANGED", "I deliberately left it unchanged."],
    ["DELEGATED", "I expected another review opportunity or another person or system to resolve it."],
    ["OTHER", "Something else"],
    ["DO_NOT_REMEMBER", "I do not remember."],
  ],
};

const F2_LABELS = Object.fromEntries(OPTIONS.F2);

function displayValue(projection) {
  if (!projection || projection.status === "MISSING") return "No value was recorded.";
  if (typeof projection.value === "string") return projection.value;
  return JSON.stringify(projection.value);
}

function RadioOptions({ name, options, selected, onSelect }) {
  return (
    <div className="recall-options">
      {options.map(([code, label]) => (
        <label key={code} className="recall-option">
          <input
            type="radio"
            name={name}
            value={code}
            checked={selected === code}
            onChange={() => onSelect(code)}
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
}

function FieldHeader({ probe }) {
  if (!probe) return null;
  return (
    <div className="recall-field-label">
      Field: <strong>{probe.field_label}</strong>
    </div>
  );
}

function ValueReveal({ probe }) {
  if (!probe?.values_revealed) return null;
  return (
    <div className="recall-value-reveal" aria-label="Previously shown field values">
      <p>
        When the final review began, this field showed:{" "}
        <strong>{displayValue(probe.initial_value)}</strong>
      </p>
      <p>
        When you submitted the form, it showed:{" "}
        <strong>{displayValue(probe.submitted_value)}</strong>
      </p>
    </div>
  );
}

function RecallQuestionForm({ state, onSave, saving }) {
  const question = state.next_question;
  const questionId = question.question_id;
  const probe = question.field_id
    ? state.probes.find((item) => item.field_id === question.field_id)
    : null;
  const presentedAtRef = useRef(new Date().toISOString());
  const startedAtRef = useRef(Date.now());
  const frozenPayloadRef = useRef(null);
  const [selected, setSelected] = useState("");
  const [selectedMany, setSelectedMany] = useState([]);
  const [text, setText] = useState("");
  const [attempted, setAttempted] = useState(false);

  const f2bOptions = useMemo(() => {
    const selectedSources = (state.f2b_options || []).map((code) => [
      code,
      F2_LABELS[code] || code,
    ]);
    return [
      ...selectedSources,
      ["NO_PRIMARY", "None was the main basis."],
      ["DO_NOT_REMEMBER", "I do not remember."],
    ];
  }, [state.f2b_options]);

  const toggleF2 = (code) => {
    setSelectedMany((current) => {
      if (code === "NO_BASIS" || code === "DO_NOT_REMEMBER") {
        return current.length === 1 && current[0] === code ? [] : [code];
      }
      const withoutExclusive = current.filter(
        (value) => value !== "NO_BASIS" && value !== "DO_NOT_REMEMBER"
      );
      return withoutExclusive.includes(code)
        ? withoutExclusive.filter((value) => value !== code)
        : [...withoutExclusive, code];
    });
  };

  const valid = (() => {
    if (questionId === "G0") return selected === "DO_NOT_REMEMBER" || text.trim().length > 0;
    if (questionId === "F2") {
      return selectedMany.length > 0
        && (!selectedMany.includes("OTHER") || text.trim().length > 0);
    }
    if (questionId === "F5a") return true;
    if (questionId === "F3") {
      return selected === "NO_SPECIFIC_TARGET"
        || selected === "DO_NOT_REMEMBER"
        || text.trim().length > 0;
    }
    return Boolean(selected) && (selected !== "OTHER" || text.trim().length > 0);
  })();

  const submit = (event) => {
    event.preventDefault();
    if (!valid || saving) return;
    if (!frozenPayloadRef.current) {
      const payload = {
        probe_set_id: state.probe_set_id,
        field_id: question.field_id,
        question_id: questionId,
        presented_at_client_ts: presentedAtRef.current,
        answered_at_client_ts: new Date().toISOString(),
        response_latency_ms: Math.max(0, Date.now() - startedAtRef.current),
      };
      if (questionId === "F2") {
        payload.response_codes = [...selectedMany];
        if (selectedMany.includes("OTHER")) payload.other_text = text.trim();
      } else if (questionId === "G0") {
        payload.response_code = selected === "DO_NOT_REMEMBER" ? selected : "TEXT";
        if (payload.response_code === "TEXT") payload.other_text = text.trim();
      } else if (questionId === "F3") {
        payload.response_code = selected || "TEXT";
        if (payload.response_code === "TEXT") payload.other_text = text.trim();
      } else if (questionId === "F5a") {
        payload.response_code = selected === "DO_NOT_REMEMBER" ? selected : "TEXT";
        if (payload.response_code === "TEXT") payload.other_text = text.trim();
      } else {
        payload.response_code = selected;
        if (selected === "OTHER") payload.other_text = text.trim();
      }
      frozenPayloadRef.current = Object.freeze(payload);
      setAttempted(true);
    }
    onSave(frozenPayloadRef.current);
  };

  return (
    <form className="recall-question-card" onSubmit={submit}>
      <fieldset className="recall-answer-fields" disabled={attempted || saving}>
        {questionId === "G0" && <p className="recall-intro">{INTRO}</p>}
        <FieldHeader probe={probe} />
        <ValueReveal probe={probe} />

      {questionId === "G0" && (
        <>
          <h2>Please briefly describe how you went through the completed form before submitting it. What, if anything, did you do to decide whether values could stay as they were or needed changing?</h2>
          <textarea
            aria-label="Final review description"
            rows="4"
            value={text}
            disabled={selected === "DO_NOT_REMEMBER"}
            onChange={(event) => {
              setText(event.target.value);
              if (event.target.value) setSelected("");
            }}
          />
          <label className="recall-option">
            <input
              type="checkbox"
              checked={selected === "DO_NOT_REMEMBER"}
              onChange={(event) => {
                setSelected(event.target.checked ? "DO_NOT_REMEMBER" : "");
                if (event.target.checked) setText("");
              }}
            />
            <span>I do not remember.</span>
          </label>
        </>
      )}

      {questionId === "F1" && (
        <>
          <h2>During the final review, which option best describes what you did with this specific field?</h2>
          <RadioOptions name="f1" options={OPTIONS.F1} selected={selected} onSelect={setSelected} />
          {selected === "OTHER" && (
            <input
              aria-label="Something else"
              type="text"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          )}
        </>
      )}

      {questionId === "F2" && (
        <>
          <h2>What information, if any, did you rely on when deciding whether to leave or change this field? Select all that apply.</h2>
          <div className="recall-options">
            {OPTIONS.F2.map(([code, label]) => (
              <label key={code} className="recall-option">
                <input
                  type="checkbox"
                  checked={selectedMany.includes(code)}
                  onChange={() => toggleF2(code)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {selectedMany.includes("OTHER") && (
            <input
              aria-label="Something else"
              type="text"
              value={text}
              onChange={(event) => setText(event.target.value)}
            />
          )}
        </>
      )}

      {questionId === "F2b" && (
        <>
          <h2>Which one, if any, was the main basis for your decision?</h2>
          <RadioOptions name="f2b" options={f2bOptions} selected={selected} onSelect={setSelected} />
        </>
      )}

      {questionId === "F3" && (
        <>
          <h2>Without looking anything up, what value did you believe belonged in this field when you submitted the form?</h2>
          <input
            aria-label="Remembered field value"
            type="text"
            value={text}
            disabled={Boolean(selected)}
            onChange={(event) => {
              setText(event.target.value);
              if (event.target.value) setSelected("");
            }}
          />
          <RadioOptions
            name="f3"
            options={[
              ["NO_SPECIFIC_TARGET", "I did not form a specific view about the value."],
              ["DO_NOT_REMEMBER", "I do not remember."],
            ]}
            selected={selected}
            onSelect={(code) => {
              setSelected(code);
              setText("");
            }}
          />
        </>
      )}

      {questionId === "F4" && (
        <>
          <h2>At the time you reviewed this field, which option best describes what you thought about the value initially shown?</h2>
          <RadioOptions name="f4" options={OPTIONS.F4} selected={selected} onSelect={setSelected} />
          {selected === "OTHER" && (
            <input aria-label="Something else" type="text" value={text} onChange={(event) => setText(event.target.value)} />
          )}
        </>
      )}

      {questionId === "F5" && (
        <>
          <h2>What best describes what you did or planned to do next?</h2>
          <RadioOptions name="f5" options={OPTIONS.F5} selected={selected} onSelect={setSelected} />
          {selected === "OTHER" && (
            <input aria-label="Something else" type="text" value={text} onChange={(event) => setText(event.target.value)} />
          )}
        </>
      )}

      {questionId === "F5a" && (
        <>
          <h2>If you remember, briefly describe what you were considering when you chose that next step.</h2>
          <textarea
            aria-label="What you were considering"
            rows="3"
            value={text}
            disabled={selected === "DO_NOT_REMEMBER"}
            onChange={(event) => {
              setText(event.target.value);
              if (event.target.value) setSelected("");
            }}
          />
          <label className="recall-option">
            <input
              type="checkbox"
              checked={selected === "DO_NOT_REMEMBER"}
              onChange={(event) => {
                setSelected(event.target.checked ? "DO_NOT_REMEMBER" : "");
                if (event.target.checked) setText("");
              }}
            />
            <span>I do not remember.</span>
          </label>
        </>
      )}

      </fieldset>
      <button className="primary" type="submit" disabled={!valid || saving}>
        {saving ? "Saving…" : attempted ? "Retry saving" : "Continue"}
      </button>
    </form>
  );
}

export function RecallQuestionnaire({
  loadState,
  saveResponse,
  completeRecall,
  onComplete,
  onTelemetry,
}) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const completionStartedRef = useRef(false);
  const announcedQuestionsRef = useRef(new Set());
  const questionnaireVersion = state?.questionnaire_version;
  const probeSetId = state?.probe_set_id;

  useEffect(() => {
    let cancelled = false;
    loadState()
      .then((next) => {
        if (!cancelled) setState(next);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError?.message || "The recall questions could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [loadState]);

  useEffect(() => {
    if (!probeSetId || !onTelemetry) return;
    onTelemetry({
      event_type: "recall_module_start",
      surface: "recall",
      questionnaire_version: questionnaireVersion,
      probe_set_id: probeSetId,
    });
  }, [onTelemetry, probeSetId, questionnaireVersion]);

  useEffect(() => {
    const question = state?.next_question;
    if (!question || !onTelemetry) return;
    const key = `${question.field_id || "GLOBAL"}:${question.question_id}`;
    if (announcedQuestionsRef.current.has(key)) return;
    announcedQuestionsRef.current.add(key);
    onTelemetry({
      event_type: "recall_question_presented",
      surface: "recall",
      questionnaire_version: state.questionnaire_version,
      probe_set_id: state.probe_set_id,
      field_id: question.field_id,
      question_id: question.question_id,
    });
  }, [onTelemetry, state]);

  useEffect(() => {
    if (!state || state.next_question || completionStartedRef.current) return;
    completionStartedRef.current = true;
    if (state.status === "completed") {
      onComplete(state);
      return;
    }
    setSaving(true);
    completeRecall()
      .then((completed) => {
        setState(completed);
        onTelemetry?.({
          event_type: "recall_module_complete",
          surface: "recall",
          questionnaire_version: completed.questionnaire_version,
          probe_set_id: completed.probe_set_id,
        });
        onComplete(completed);
      })
      .catch((completionError) => {
        completionStartedRef.current = false;
        setError(completionError?.message || "The recall responses could not be completed.");
      })
      .finally(() => setSaving(false));
  }, [completeRecall, onComplete, onTelemetry, state]);

  const save = async (payload) => {
    setSaving(true);
    setError("");
    try {
      const next = await saveResponse(payload);
      setState(next);
    } catch (saveError) {
      setError(saveError?.message || "Your answer could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="recall-root" aria-label="Post-submit recall questions">
      <header className="recall-header">
        <h1>Final review questions</h1>
        <p>Your report has been submitted and can no longer be changed.</p>
      </header>
      {error && <div className="recall-error" role="alert">{error}</div>}
      {!state && !error && <div className="recall-loading">Loading questions…</div>}
      {state?.next_question && (
        <RecallQuestionForm
          key={`${state.next_question.field_id || "GLOBAL"}:${state.next_question.question_id}`}
          state={state}
          onSave={save}
          saving={saving}
        />
      )}
      {state && !state.next_question && (
        <div className="recall-question-card" role="status">
          <h2>{state.status === "completed" ? "Responses saved" : "Saving your responses…"}</h2>
          <p>Please wait while the study returns you to the survey.</p>
        </div>
      )}
    </main>
  );
}

export default RecallQuestionnaire;
